import { createSlice, nanoid, PayloadAction } from '@reduxjs/toolkit';
import {
  FormState,
  QueryParamState,
  FormFieldState,
  FormFieldOptionState,
  FieldType,
  ValidationState,
  DateFormatState,
} from '../../types/form';
import { NEW_LOGO_BASE64, NEW_ICON_BASE64 } from '../../../utils/base64';
import { BrandColors } from '../../../components/client/pass-templates-new/custom-download-forms/formTheme';

export const initialFormState: FormState = {
  enabled: false,
  slug: '',
  slugTouched: false,
  formFields: [],
  backgroundColor: '#F8F8F8',
  componentFillColor: '#FFFFFF',
  componentFontColor: '#252525',
  buttonText: 'Get your pass',
  buttonColor: '#FF4935',
  buttonCtaColor: '#FFFFFF',
  formLogoImage: NEW_LOGO_BASE64,
  formFaviconImage: NEW_ICON_BASE64,
  formFaviconUrl: '',
  footerEnabled: false,
  footerContent:
    'Powered by <a href="https://www.passentry.com" target="_blank" style="text-decoration: underline">PassEntry</a>',
  footerColor: '#4C4C4C',
  queryParams: [],
  dataProcessingConsentText: '',
  dataProcessingConsentRequired: false,
};

const formSlice = createSlice({
  name: 'form',
  initialState: initialFormState,
  reducers: {
    addFormField: (
      state: FormState,
      action: PayloadAction<{ asGroupTagOnly?: boolean } | undefined>
    ) => {
      const asGroupTagOnly = action.payload?.asGroupTagOnly ?? false;
      if (asGroupTagOnly) {
        state.formFields.push({
          _clientId: nanoid(),
          id: 'none',
          label: '',
          name: '',
          required: false,
          extId: false,
          type: 'dropdown',
          placeholder: 'Select an option...',
          options: [{ id: nanoid(), label: '' }],
        });
      } else {
        state.formFields.push({
          _clientId: nanoid(),
          id: '',
          label: '',
          name: '',
          required: false,
          extId: false,
        });
      }
    },
    removeFormField: (state: FormState, action: PayloadAction<string>) => {
      state.formFields = state.formFields.filter(
        (f) => f._clientId !== action.payload && f.id !== action.payload
      );
    },
    setFormField: <K extends keyof FormState>(
      state: FormState,
      action: PayloadAction<{ field: K; value: FormState[K] }>
    ) => {
      state[action.payload.field] = action.payload.value;
    },
    setFormFieldProp: <K extends keyof FormState>(
      state: FormState,
      action: PayloadAction<{ field: K; value: FormState[K] }>
    ) => {
      state[action.payload.field] = action.payload.value;
    },
    addQueryParam: (state: FormState) => {
      state.queryParams.push({
        _clientId: nanoid(),
        id: '',
        name: '',
        required: false,
      });
    },
    removeQueryParam: (state: FormState, action: PayloadAction<string>) => {
      state.queryParams = state.queryParams.filter(
        (q) => q._clientId !== action.payload
      );
    },
    setQueryParamProp: <K extends keyof QueryParamState>(
      state: FormState,
      action: PayloadAction<{
        queryParamClientId: string;
        key: K;
        value: QueryParamState[K];
      }>
    ) => {
      const { queryParamClientId, key, value } = action.payload;
      const queryParams = state.queryParams;
      const queryParamIndex = queryParams.findIndex(
        (q) => q._clientId === queryParamClientId
      );
      if (queryParamIndex !== -1) {
        state.queryParams[queryParamIndex] = {
          ...queryParams[queryParamIndex],
          [key]: value,
        };
      }
    },
    setQueryParamConnectField: (
      state: FormState,
      action: PayloadAction<{
        queryParamClientId: string;
        templateFieldId: string;
        templateFieldLabel: string;
      }>
    ) => {
      const { queryParamClientId, templateFieldId, templateFieldLabel } =
        action.payload;
      const idx = state.queryParams.findIndex(
        (q) => q._clientId === queryParamClientId
      );
      if (idx !== -1) {
        state.queryParams[idx].id = templateFieldId;
        state.queryParams[idx].name = templateFieldLabel;
      }
    },
    setFormFieldItemProp: (
      state: FormState,
      action: PayloadAction<{
        fieldId: string;
        key: keyof FormFieldState;
        value:
          | string
          | boolean
          | FieldType
          | ValidationState
          | FormFieldOptionState[]
          | DateFormatState;
      }>
    ) => {
      const { fieldId, key, value } = action.payload;
      const formFields = state.formFields;
      const fieldIndex = formFields.findIndex(
        (f) => f._clientId === fieldId || f.id === fieldId
      );
      if (fieldIndex !== -1) {
        if (key === 'extId' && value === true) {
          state.formFields.forEach((f, i) => {
            if (f._clientId !== fieldId && f.id !== fieldId && f.extId) {
              state.formFields[i] = { ...f, extId: false };
            }
          });
        }
        const field = state.formFields[fieldIndex];
        state.formFields[fieldIndex] = {
          ...field,
          [key]: value,
        };
        if (key === 'type') {
          const newType = value as FieldType;
          const f = state.formFields[fieldIndex];
          if (newType === 'input') {
            f.validation = { name: 'none', pattern: '', message: '' };
            delete f.options;
            delete f.placeholder;
            delete f.dateFormat;
          } else if (newType === 'dropdown') {
            f.options = [{ id: nanoid(), label: '' }];
            f.placeholder = 'Select an option...';
            delete f.validation;
            delete f.dateFormat;
          } else if (newType === 'date') {
            f.dateFormat = '%d/%m/%Y';
            delete f.validation;
            delete f.options;
            delete f.placeholder;
          }
        }
      }
    },
    setFormFieldLinkToTemplateField: (
      state: FormState,
      action: PayloadAction<{
        fieldId: string;
        templateFieldId: string;
        templateFieldLabel: string;
        templateFieldName: string;
      }>
    ) => {
      const {
        fieldId,
        templateFieldId,
        templateFieldLabel,
        templateFieldName,
      } = action.payload;
      const fieldIndex = state.formFields.findIndex(
        (f) => f._clientId === fieldId || f.id === fieldId
      );
      if (fieldIndex !== -1) {
        const current = state.formFields[fieldIndex];
        state.formFields[fieldIndex] = {
          ...current,
          id: templateFieldId,
          label:
            templateFieldId === 'none' ? current.label : templateFieldLabel,
          name: templateFieldName,
        };
      }
    },
    addFormFieldOption: (
      state: FormState,
      action: PayloadAction<{ fieldId: string }>
    ) => {
      const { fieldId } = action.payload;
      const fieldIndex = state.formFields.findIndex(
        (f) => f._clientId === fieldId || f.id === fieldId
      );
      if (fieldIndex !== -1) {
        const options = state.formFields[fieldIndex].options ?? [];
        state.formFields[fieldIndex].options = [
          ...options,
          { id: nanoid(), label: '' },
        ];
      }
    },
    removeFormFieldOption: (
      state: FormState,
      action: PayloadAction<{ fieldId: string; optionId: string }>
    ) => {
      const { fieldId, optionId } = action.payload;
      const fieldIndex = state.formFields.findIndex(
        (f) => f._clientId === fieldId || f.id === fieldId
      );
      if (fieldIndex !== -1) {
        state.formFields[fieldIndex].options = (
          state.formFields[fieldIndex].options ?? []
        ).filter((opt) => opt.id !== optionId);
      }
    },
    setFormFieldOptionProp: (
      state: FormState,
      action: PayloadAction<{
        fieldId: string;
        optionId: string;
        key: keyof FormFieldOptionState;
        value: string | boolean;
      }>
    ) => {
      const { fieldId, optionId, key, value } = action.payload;
      const fieldIndex = state.formFields.findIndex(
        (f) => f._clientId === fieldId || f.id === fieldId
      );
      if (fieldIndex !== -1) {
        const options = state.formFields[fieldIndex].options ?? [];
        const optionIndex = options.findIndex((opt) => opt.id === optionId);
        if (optionIndex !== -1) {
          state.formFields[fieldIndex].options[optionIndex] = {
            ...options[optionIndex],
            [key]: value,
          };
        }
      }
    },
    prefillFormBranding: (
      state: FormState,
      action: PayloadAction<{ colors: BrandColors; logo?: string }>
    ) => {
      Object.assign(state, action.payload.colors);
      if (action.payload.logo) state.formLogoImage = action.payload.logo;
    },
    resetForm: () => initialFormState,
    setForm: (_, action: PayloadAction<FormState>) => action.payload,
  },
});

export const {
  addFormField,
  removeFormField,
  setFormField,
  setFormFieldProp,
  addQueryParam,
  removeQueryParam,
  setQueryParamProp,
  setQueryParamConnectField,
  setFormFieldItemProp,
  setFormFieldLinkToTemplateField,
  addFormFieldOption,
  removeFormFieldOption,
  setFormFieldOptionProp,
  prefillFormBranding,
  resetForm,
  setForm,
} = formSlice.actions;
export default formSlice.reducer;
